import Seo from "../../components/Seo";
import { seoFromPublicPage } from "../../seo/publicPages";
import ArticleLayout from "./ArticleLayout";
import RelatedGuides from "./RelatedGuides";

export default function ModelFoaieParcursPage() {
  return (
    <>
      <Seo {...seoFromPublicPage("/model-foaie-parcurs")} />
      <ArticleLayout
        eyebrow="Model practic"
        title="Model foaie de parcurs lunară: structură și exemplu completat"
        lead="Nu există un formular tipizat impus de ANAF, dar există o structură pe care firmele moderne au standardizat-o pentru a trece ușor de controale. Iată cum arată o foaie de parcurs lunară corectă, secțiune cu secțiune."
      >
        <h2>De ce „lunară" și nu „zilnică"</h2>
        <p>
          Legea nu impune o frecvență. O <strong>singură foaie pe lună</strong>, per
          vehicul, cu toate deplasările într-un tabel, este acceptată legal. Această
          variantă este preferată de majoritatea firmelor mici și a PFA-urilor pentru că:
        </p>
        <ul>
          <li>reduce volumul de hârtie / fișiere de 30 ori față de varianta zilnică;</li>
          <li>permite reconciliere unică la sfârșitul lunii cu odometrul și fișa de combustibil;</li>
          <li>se aliniază natural cu perioada fiscală TVA (lună sau trimestru).</li>
        </ul>

        <h2>Cele 4 elemente obligatorii (minimul legal)</h2>
        <p>
          Conform <strong>pct. 68 alin. (2)</strong> din Normele de aplicare a art. 298
          din Codul Fiscal, foaia trebuie să cuprindă:
        </p>
        <ol>
          <li>categoria vehiculului;</li>
          <li>scopul și locul deplasării;</li>
          <li>kilometrii parcurși;</li>
          <li>norma proprie de consum carburant pe kilometru parcurs.</li>
        </ol>
        <p>
          Acestea sunt obligatoriile. Tot ce urmează mai jos sunt elemente recomandate
          care fac foaia mai ușor de apărat la control.
        </p>

        <h2>Structura unei foi lunare moderne</h2>

        <h3>1. Antetul</h3>
        <ul>
          <li>Denumirea firmei / PFA-ului și CUI</li>
          <li>Marca, modelul, numărul de înmatriculare, categoria vehiculului</li>
          <li>Numele șoferului / utilizatorului</li>
          <li>Perioada acoperită (ex. 01.06–30.06.2026)</li>
          <li>Norma proprie de consum (ex. 7,5 L/100 km)</li>
          <li>Kilometri la începutul perioadei (de pe odometru)</li>
          <li>Combustibil în rezervor la începutul perioadei (litri)</li>
        </ul>

        <h3>2. Tabelul rutelor</h3>
        <p>
          Câte un rând per cursă efectuată, cu coloanele:
        </p>
        <div className="article-doc__table-wrap">
          <table className="article-doc__table">
            <thead>
              <tr>
                <th>Nr.</th>
                <th>Data</th>
                <th>Plecare</th>
                <th>Sosire</th>
                <th>Scopul</th>
                <th>Km</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>03.06.2026</td>
                <td>Sediu Timișoara</td>
                <td>SC Alpha SRL, Arad</td>
                <td>Livrare comandă #1247</td>
                <td>62</td>
              </tr>
              <tr>
                <td>2</td>
                <td>03.06.2026</td>
                <td>SC Alpha SRL, Arad</td>
                <td>Sediu Timișoara</td>
                <td>Întoarcere</td>
                <td>62</td>
              </tr>
              <tr>
                <td>3</td>
                <td>05.06.2026</td>
                <td>Sediu Timișoara</td>
                <td>ANAF Timișoara</td>
                <td>Depunere declarație 100</td>
                <td>8</td>
              </tr>
              <tr>
                <td>…</td>
                <td>…</td>
                <td>…</td>
                <td>…</td>
                <td>…</td>
                <td>…</td>
              </tr>
              <tr>
                <td colSpan={5}><strong>Total km luna iunie</strong></td>
                <td><strong>1.842</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>3. Tabelul alimentărilor (recomandat, nu obligatoriu)</h3>
        <p>
          Permite reconcilierea cu bonurile de combustibil și demonstrează coerența
          consumului. Coloane:
        </p>
        <ul>
          <li>Data alimentării</li>
          <li>Litri alimentați</li>
          <li>Preț/L (lei)</li>
          <li>Total plătit</li>
          <li>Benzinărie (denumire și localitate)</li>
        </ul>

        <h3>4. Sumarul lunii</h3>
        <ul>
          <li>Total km parcurși în lună</li>
          <li>Kilometri la sfârșitul perioadei (odometru final)</li>
          <li>Consum estimat (km × norma / 100)</li>
          <li>Total litri alimentați</li>
          <li>Combustibil în rezervor la sfârșitul perioadei</li>
        </ul>
        <div className="article-doc__callout">
          <strong>Validarea de bază</strong>
          <p>
            La final de lună trebuie să tornească <em>aproximativ</em>: <br />
            <strong>combustibil inițial + alimentări − consum estimat = combustibil final</strong>.
            Diferențele mici sunt normale (variații consum real); diferențele mari de zeci
            de litri ridică probleme.
          </p>
        </div>

        <h3>5. Semnătura</h3>
        <p>
          Foaia se semnează de șofer și, dacă este cazul, de responsabilul tehnic /
          administrator. Pentru PFA, semnătura titularului este suficientă.
        </p>

        <h2>Exemplu de antet completat</h2>
        <div className="article-doc__callout">
          <strong>Exemplu — SRL cu 3 vehicule, iunie 2026</strong>
          <p>
            Firma: <em>Alpha Construct SRL</em>, CUI: <em>RO12345678</em><br />
            Vehicul: <em>Dacia Duster 1.5 dCi, TM-12-AAA, autoturism</em><br />
            Șofer: <em>Ionescu Andrei</em><br />
            Perioada: <em>01.06.2026 – 30.06.2026</em><br />
            Normă de consum: <em>6,5 L/100 km (mixt)</em><br />
            Km la 01.06.2026: <em>78.420</em><br />
            Combustibil în rezervor la 01.06.2026: <em>18 L</em>
          </p>
        </div>

        <h2>Foaie de parcurs vs. FAZ — diferența</h2>
        <p>
          <strong>Fișa de Activități Zilnice (FAZ)</strong> este un document complementar,
          nu un înlocuitor. Diferența:
        </p>
        <div className="article-doc__table-wrap">
          <table className="article-doc__table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Ce conține</th>
                <th>Cui îi folosește</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Foaie de parcurs</td>
                <td>rute, scop, km parcurși per cursă</td>
                <td>justificare deducere fiscală (obligatoriu)</td>
              </tr>
              <tr>
                <td>FAZ</td>
                <td>stoc combustibil zilnic, consum normat vs. real, alimentări</td>
                <td>contabilitate internă, comparație consum (opțional, foarte util)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          În aplicațiile moderne, FAZ-ul se generează <strong>automat</strong> din foaia
          de parcurs și alimentări, fără efort suplimentar.
        </p>

        <h2>Format: hârtie, Excel sau PDF generat automat</h2>
        <ul>
          <li>
            <strong>Hârtie</strong> — carnete preimprimate, completate manual. Funcționează
            pentru 1 vehicul folosit ocazional; obositor și predispus la erori pentru flote.
          </li>
          <li>
            <strong>Excel</strong> — șablon cu formule pentru sumar și consum. Cea mai
            răspândită opțiune, dar necesită disciplină la completare.
          </li>
          <li>
            <strong>Aplicație cloud / PDF generat automat</strong> — completare rapidă,
            nomenclator de adrese, calcul automat km via Google Maps, validare coerență,
            export lunar PDF. Cea mai mică șansă de eroare.
          </li>
        </ul>

        <h2>Ce să eviți la întocmire</h2>
        <ul>
          <li>Scop generic („interes serviciu", „deplasare").</li>
          <li>Lipsa odometrului la început și sfârșit de lună.</li>
          <li>Norma de consum copiată din cartea tehnică fără ajustare reală.</li>
          <li>Foi completate la 6 luni cu același pix și aceeași grafie.</li>
          <li>Lipsa semnăturii pe foaie.</li>
        </ul>
        <RelatedGuides currentPath="/model-foaie-parcurs" />
      </ArticleLayout>
    </>
  );
}
